import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { GetBeeNumberAuthenicatedUser, GetCountryCode } from '../stores/SettingsStore'
import { PwsProfileAddressGet, PwsProfileAddressSave, PwsProfileAddressDelete } from '../api/AccountInfo'
import type { IAddress } from '../types/AccountInfoTypes'
import type { IAddressVerificationListItem } from '../types/CountryCultureTypes'
import { GetCountriesFromLocalStorageOrApi } from '../services/CountryCultureService'
import Swal from 'sweetalert2'
import { useFormValidation } from './useFormValidation'
import { handleError } from '../services/ErrorService'
import { addressSchema } from '../validators/ValidationSchemas'

export function useBillingAddress() {
  const { t } = useI18n()
  const logger = useLogger()
  const { errors: addressErrors, validateAll: validateAddressAll, clearErrors: clearAddressErrors } = useFormValidation()

  // Address management state
  const showAddressSelection = ref(false)
  const addressSelectionType = ref<'billing' | 'shipping'>('billing')
  const showBillingAddressForm = ref(false)
  const editingBillingAddress = ref(false)
  const currentAddressId = ref<number | null>(null)

  // Address verification state
  const addressVerificationShow = ref(false)
  const verifyAddress = ref<IAddressVerificationListItem[]>([])
  const addressVerifyError = ref(false)
  const addressList = ref<IAddress[]>([])
  const billingAddress = ref<IAddress | null>(null)
  const shippingAddress = ref<IAddress | null>(null)
  const isLoadingAddresses = ref<boolean>(false)
  const countries = ref<Array<{ text: string, id: string }>>([])

  const billingAddressForm = ref({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    houseNumber: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  })
  const billingAddressNameDisplay = ref('')

  // Determine which addressId is the current default for the type being selected
  const defaultAddressIdForType = computed(() =>
    addressSelectionType.value === 'billing'
      ? billingAddress.value?.addressId ?? null
      : shippingAddress.value?.addressId ?? null
  )

  function isDefaultAddress(address: IAddress): boolean {
    return address.addressId != null && address.addressId === defaultAddressIdForType.value
  }

  // Address list with the current default for the selected type pinned to the top
  const sortedAddressList = computed<IAddress[]>(() => {
    const defaultId = defaultAddressIdForType.value
    return [...addressList.value].sort((a, b) => {
      const aIsDefault = a.addressId === defaultId ? 1 : 0
      const bIsDefault = b.addressId === defaultId ? 1 : 0
      return bIsDefault - aIsDefault
    })
  })

  // Address functions
  function changeAddress(type: 'billing' | 'shipping') {
    addressSelectionType.value = type
    showAddressSelection.value = true
  }

  async function addNewAddress() {
    editingBillingAddress.value = false
    currentAddressId.value = null // New address, no ID

    // Get default country code from settings
    const defaultCountry = await GetCountryCode()

    billingAddressForm.value = {
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      houseNumber: '',
      city: '',
      state: '',
      postalCode: '',
      country: defaultCountry
    }
    billingAddressNameDisplay.value = ''
    showBillingAddressForm.value = true
    showAddressSelection.value = false
  }

  function selectExistingAddress(address: IAddress) {
    // Set the selected address as the default for the current type
    const isDefaultBilling = addressSelectionType.value === 'billing'
    const isDefaultShipping = addressSelectionType.value === 'shipping'

    setAddressAsDefault(address, isDefaultBilling, isDefaultShipping)
  }

  async function setAddressAsDefault(address: IAddress, isDefaultBilling: boolean, isDefaultShipping: boolean) {
    try {
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for address update')
        return
      }

      const response = await PwsProfileAddressSave({
        beeNumber,
        addressId: address.addressId,
        address: {
          addressId: address.addressId,
          firstName: address.firstName,
          lastName: address.lastName,
          addressLine1: address.addressLine1,
          addressLine2: address.addressLine2,
          houseNumber: address.houseNumber,
          city: address.city,
          stateCode: address.stateCode,
          postalCode: address.postalCode,
          countryCode: address.countryCode
        },
        isDefaultBilling,
        isDefaultShipping,
        isDefaultMailing: false,
        SkipAddressValidation: false
      })

      if (response?.success || !response?.responseStatus?.errorCode) {
        // Update local state
        if (isDefaultBilling) {
          billingAddress.value = address
        }
        if (isDefaultShipping) {
          shippingAddress.value = address
        }

        showAddressSelection.value = false

        Swal.fire({
          title: t('accountInfo.billingAddress.addressUpdated'),
          icon: 'success',
          confirmButtonText: t('buttons.Okay')
        })
      } else {
        Swal.fire({
          title: t('labels.Error'),
          text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.updateError'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
      }
    } catch (error) {
      logger.error('Address update failed:', error)
      await handleError(error, t)
    }
  }

  function cancelAddressSelection() {
    showAddressSelection.value = false
  }

  // Address verification handlers
  function useEnteredAddressClick() {
    logger.debug('useEnteredAddressClick - proceeding with entered address')
    addressVerificationShow.value = false

    // Save the address again but skip validation this time
    saveBillingAddressWithSkipValidation()
  }

  function useSelectedAddressClick(selectedAddress: IAddress) {
    logger.debug('useSelectedAddressClick', selectedAddress)

    // Update the form with the selected address
    billingAddressForm.value = {
      ...billingAddressForm.value,
      address1: selectedAddress.addressLine1 || '',
      address2: selectedAddress.addressLine2 || '',
      houseNumber: selectedAddress.houseNumber || '',
      city: selectedAddress.city || '',
      state: selectedAddress.stateCode || '',
      postalCode: selectedAddress.postalCode || '',
      country: selectedAddress.countryCode || ''
    }

    addressVerificationShow.value = false

    // Save the address with the corrected information
    saveBillingAddressWithSkipValidation()
  }

  function reEnterAddressClick() {
    logger.debug('reEnterAddressClick - returning to address form')
    addressVerificationShow.value = false
    // Keep the form open so user can edit the address
  }

  async function saveBillingAddressWithSkipValidation() {
    try {
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for address save')
        return
      }

      const addressData = {
        addressId: currentAddressId.value || undefined,
        firstName: billingAddressForm.value.firstName,
        lastName: billingAddressForm.value.lastName,
        addressLine1: billingAddressForm.value.address1,
        addressLine2: billingAddressForm.value.address2 || undefined,
        houseNumber: billingAddressForm.value.houseNumber || undefined,
        city: billingAddressForm.value.city,
        stateCode: billingAddressForm.value.state,
        postalCode: billingAddressForm.value.postalCode,
        countryCode: billingAddressForm.value.country
      }

      const isNewAddress = !currentAddressId.value
      const isDefaultBilling = isNewAddress && addressSelectionType.value === 'billing'
      const isDefaultShipping = isNewAddress && addressSelectionType.value === 'shipping'

      const response = await PwsProfileAddressSave({
        beeNumber,
        addressId: currentAddressId.value,
        address: addressData,
        isDefaultBilling,
        isDefaultShipping,
        isDefaultMailing: false,
        SkipAddressValidation: true // Skip validation this time
      })

      if (response?.success || !response?.responseStatus?.errorCode) {
        Swal.fire({
          title: t('accountInfo.billingAddress.saveSuccess'),
          icon: 'success',
          confirmButtonText: t('buttons.Okay')
        })

        showBillingAddressForm.value = false
        await loadAddressList()
      } else {
        Swal.fire({
          title: t('labels.Error'),
          text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.saveError'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
      }
    } catch (error) {
      logger.error('Billing address save failed:', error)
      await handleError(error, t)
    }
  }

  function onBillingAddressChange(updatedAddress: {
    addressLine1: string
    addressLine2: string
    houseNumber: string
    city: string
    stateCode: string
    postalCode: string
    countryCode: string
    firstName: string
    lastName: string
  }) {
    billingAddressForm.value = {
      ...billingAddressForm.value,
      firstName: updatedAddress.firstName,
      lastName: updatedAddress.lastName,
      address1: updatedAddress.addressLine1,
      address2: updatedAddress.addressLine2,
      houseNumber: updatedAddress.houseNumber,
      city: updatedAddress.city,
      state: updatedAddress.stateCode,
      postalCode: updatedAddress.postalCode,
      country: updatedAddress.countryCode
    }
  }

  async function saveBillingAddress(addressControlRef: { validate: () => boolean } | null) {
    try {
      // Validate AddressControl fields first
      if (addressControlRef && !addressControlRef.validate()) {
        return
      }

      const schema = addressSchema(t, {
        firstName: billingAddressForm.value.firstName?.trim() ?? '',
        address1: billingAddressForm.value.address1?.trim() ?? '',
        city: billingAddressForm.value.city?.trim() ?? '',
        postalCode: billingAddressForm.value.postalCode?.trim() ?? '',
        country: billingAddressForm.value.country?.trim() ?? '',
        state: billingAddressForm.value.state?.trim(),
      })

      clearAddressErrors()
      if (!validateAddressAll(schema)) {
        const firstError = Object.values(addressErrors.value).find(e => !!e)
        Swal.fire({
          title: t('labels.Error'),
          text: firstError ?? t('accountInfo.billingAddress.requiredFields'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
        return
      }

      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for address save')
        return
      }

      logger.debug('Saving billing address:', billingAddressForm.value)

      const addressData = {
        addressId: currentAddressId.value || undefined,
        firstName: billingAddressForm.value.firstName,
        lastName: billingAddressForm.value.lastName,
        addressLine1: billingAddressForm.value.address1,
        addressLine2: billingAddressForm.value.address2 || undefined,
        houseNumber: billingAddressForm.value.houseNumber || undefined,
        city: billingAddressForm.value.city,
        stateCode: billingAddressForm.value.state,
        postalCode: billingAddressForm.value.postalCode,
        countryCode: billingAddressForm.value.country
      }

      // When creating a new address, set it as default for the current address type
      const isNewAddress = !currentAddressId.value
      const isDefaultBilling = isNewAddress && addressSelectionType.value === 'billing'
      const isDefaultShipping = isNewAddress && addressSelectionType.value === 'shipping'

      const response = await PwsProfileAddressSave({
        beeNumber,
        addressId: currentAddressId.value,
        address: addressData,
        isDefaultBilling,
        isDefaultShipping,
        isDefaultMailing: false,
        SkipAddressValidation: false
      })

      if (response?.addressValidationMessageId) {
        logger.debug('Setting addressVerificationShow to true')
        // Convert the response to the expected format for VerifyAddressPopup if valid addresses exist
        if (response?.validAddresses && response.validAddresses.length > 0) {
          verifyAddress.value = response.validAddresses.map((addr: {
            addressLine1: string
            addressLine2: string
            city: string
            stateCode: string
            postalCode: string
            countryCode: string
            addressGoodDate?: string
            latitude?: number
            longitude?: number
          }, index: number) => ({
            addressId: index + 1, // Use index as ID since the response doesn't include addressId
            addressLine1: addr.addressLine1,
            addressLine2: addr.addressLine2 || '',
            houseNumber: null,
            city: addr.city,
            stateCode: addr.stateCode,
            postalCode: addr.postalCode,
            countryCode: addr.countryCode,
            addressGoodDate: addr.addressGoodDate || new Date().toISOString()
          }))
        } else {
          // No valid addresses returned, but validation failed - show empty array
          verifyAddress.value = []
        }

        addressVerifyError.value = true
        addressVerificationShow.value = true
        return
      }

      if (response?.success || !response?.responseStatus?.errorCode) {
        Swal.fire({
          title: t('accountInfo.billingAddress.saveSuccess'),
          icon: 'success',
          confirmButtonText: t('buttons.Okay')
        })

        showBillingAddressForm.value = false
        // Reload address list to show updated data
        await loadAddressList()
      } else {
        Swal.fire({
          title: t('labels.Error'),
          text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.saveError'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
      }
    } catch (error) {
      logger.error('Billing address save failed:', error)
      await handleError(error, t)
    }
  }

  function cancelBillingAddressForm() {
    showBillingAddressForm.value = false
    currentAddressId.value = null
    billingAddressForm.value = {
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      houseNumber: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    }
    billingAddressNameDisplay.value = ''
  }

  async function loadBillingAddressData() {
    // Load countries for address form
    const countryData = await GetCountriesFromLocalStorageOrApi()
    if (countryData?.items) {
      countries.value = countryData.items.map(country => ({
        text: country.country,
        id: country.countryCode
      }))
    }

    // Load existing address list from API
    await loadAddressList()
  }

  async function loadAddressList() {
    try {
      isLoadingAddresses.value = true
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for address loading')
        return
      }

      const response = await PwsProfileAddressGet({ beeNumber })
      logger.debug('Address list response received:', response)

      if (response?.item) {
        // Extract all addresses
        if (response.item.allAddresses) {
          addressList.value = response.item.allAddresses.map(wrapper => ({
            ...wrapper.address,
            addressId: wrapper.addressId
          }))
        }

        // Extract current billing and shipping addresses
        if (response.item.billingAddress) {
          billingAddress.value = {
            ...response.item.billingAddress.address,
            addressId: response.item.billingAddress.addressId
          }
        }

        if (response.item.shippingAddress) {
          shippingAddress.value = {
            ...response.item.shippingAddress.address,
            addressId: response.item.shippingAddress.addressId
          }
        }

        logger.debug('Addresses loaded:', {
          billing: billingAddress.value,
          shipping: shippingAddress.value,
          all: addressList.value
        })
      } else {
        logger.warn('No address data found in response')
        addressList.value = []
        billingAddress.value = null
        shippingAddress.value = null
      }
    } catch (error) {
      logger.error('Failed to load address list:', error)
      addressList.value = []
      await handleError(error, t)
    } finally {
      isLoadingAddresses.value = false
    }
  }

  async function deleteAddress(address: IAddress) {
    try {
      // Check if this is the current billing or shipping address
      const isBillingAddress = billingAddress.value?.addressId === address.addressId
      const isShippingAddress = shippingAddress.value?.addressId === address.addressId

      if (isBillingAddress || isShippingAddress) {
        const addressType = isBillingAddress ? t('accountInfo.billingAddress.billing') : t('accountInfo.billingAddress.shipping')
        await Swal.fire({
          title: t('accountInfo.billingAddress.cannotDeleteTitle'),
          text: t('accountInfo.billingAddress.cannotDeleteMessage', { type: addressType }),
          icon: 'warning',
          confirmButtonText: t('buttons.Okay')
        })
        return
      }

      // Show confirmation dialog
      const result = await Swal.fire({
        title: t('accountInfo.billingAddress.deleteConfirmTitle'),
        text: t('accountInfo.billingAddress.deleteConfirmMessage'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('buttons.Yes'),
        cancelButtonText: t('buttons.No'),
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      })

      if (!result.isConfirmed) {
        return
      }

      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for address deletion')
        return
      }

      if (!address.addressId) {
        logger.error('Address ID is missing')
        return
      }

      const response = await PwsProfileAddressDelete({
        beeNumber,
        addressId: address.addressId
      })

      if (response?.success || !response?.responseStatus?.errorCode) {
        await Swal.fire({
          title: t('accountInfo.billingAddress.deleteSuccess'),
          icon: 'success',
          confirmButtonText: t('buttons.Okay')
        })

        // Reload address list to show updated data
        await loadAddressList()
      } else {
        await Swal.fire({
          title: t('labels.Error'),
          text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.deleteError'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
      }
    } catch (error) {
      logger.error('Address deletion failed:', error)
      await handleError(error, t)
    }
  }

  return {
    // State
    showAddressSelection,
    addressSelectionType,
    showBillingAddressForm,
    editingBillingAddress,
    currentAddressId,
    addressVerificationShow,
    verifyAddress,
    addressVerifyError,
    addressList,
    sortedAddressList,
    isDefaultAddress,
    billingAddress,
    shippingAddress,
    isLoadingAddresses,
    // countries removed - handled by AddressControl internally
    billingAddressForm,
    billingAddressNameDisplay,

    // Methods
    changeAddress,
    addNewAddress,
    selectExistingAddress,
    setAddressAsDefault,
    cancelAddressSelection,
    useEnteredAddressClick,
    useSelectedAddressClick,
    reEnterAddressClick,
    saveBillingAddressWithSkipValidation,
    onBillingAddressChange,
    saveBillingAddress,
    cancelBillingAddressForm,
    loadBillingAddressData,
    loadAddressList,
    deleteAddress
  }
}
